import React, { useState, useEffect, useRef } from 'react';
import { FlexContainer } from '@/components/containers/FlexContainer/FlexContainer';
import {
  TextField,
  Button,
  InputAdornment,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
} from '@mui/material';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import Label from '@/components/form/Label/Label';
import ReactPixel from 'react-facebook-pixel';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { format, addDays } from 'date-fns';
import { setSearchFormData } from '@/redux/actions/searchFormDataActions';
import { staticLocationsFromSearch } from '@/helpers/locationHelpers';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import './NewGeneralSearchForm.scss';

const staticLocationsSuggestions = staticLocationsFromSearch;

const fetchUserLocation = async () => {
  try {
    const response = await fetch('http://ip-api.com/json/');
    if (!response.ok) throw new Error(`Error: ${response.status}`);
    const data = await response.json();
    return { lat: data.lat, lng: data.lon };
  } catch (error) {
    console.error('Error fetching location:', error);
    return null;
  }
};

const validationSchema = Yup.object().shape({
  location: Yup.string().required('El lugar es obligatorio'),
  startDate: Yup.date().required('La fecha es obligatoria'),
  //endTime: Yup.string().required('El horario es obligatorio'),
  guests: Yup.number()
    .min(1, 'Debe haber al menos 1 persona')
    .required('Cantidad de personas requerida'),
});

const LocationSuggestions = (props) => {
  const { suggestions, setLocationToSearch, lenguage } = props;
  return (
    <FlexContainer className={'flex-column width100'}>
      {suggestions?.map((suggestion) => (
        <FlexContainer
          className={'fontGray400 grayShadow16 padlr10 padtb20 text-14 pointer'}
          key={suggestion.place_id}
          onClick={() => setLocationToSearch(suggestion)}
        >
          {lenguage === 'ES' ? suggestion.description : suggestion.description_en}
        </FlexContainer>
      ))}
    </FlexContainer>
  );
};

const NewGeneralSearchForm = (props) => {
  const { searchType } = props;
  const [modalOpen, setModalOpen] = useState(false);
  const [activeField, setActiveField] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [userLocation, setUserLocation] = useState(null);
  const [selectedSuggestion, setSelectedSuggestion] = useState(null);
  const [coordinates, setCoordinates] = useState({ lat: null, lng: null });
  const [customDateError, setCustomDateError] = useState('');

  const autocompleteService = useRef(null);

  const tomorrow = format(addDays(new Date(), 1), 'yyyy-MM-dd');

  const lenguage = useSelector((state) => state.lenguage?.lang);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    const initializeAutocomplete = () => {
      if (!autocompleteService.current && window.google) {
        autocompleteService.current = new window.google.maps.places.AutocompleteService();
      }
    };

    fetchUserLocation().then((location) => setUserLocation(location));

    if (!window.google) {
      window.addEventListener('load', initializeAutocomplete);
      return () => window.removeEventListener('load', initializeAutocomplete);
    } else {
      initializeAutocomplete();
    }
  }, []);

  useEffect(() => {
    if (modalOpen) {
      document.body.classList.add('no-scroll');
    } else {
      document.body.classList.remove('no-scroll');
    }
    return () => {
      document.body.classList.remove('no-scroll');
    };
  }, [modalOpen]);

  const initialValues = {
    location: '',
    startDate: tomorrow,
    startTime: '',
    endTime: '',
    guests: 1,
  };

  const mapFieldLabel = (field) => {
    const fieldMap = {
      location: lenguage === 'ES' ? 'el lugar' : 'the location',
      startDate: lenguage === 'ES' ? 'la fecha' : 'the date',
      endTime: lenguage === 'ES' ? 'horario' : 'schedule',
      guests: lenguage === 'ES' ? 'número de personas' : 'number of people',
    };

    return fieldMap[field] || 'Campo no encontrado';
  };

  const TitleLabel = () => {
    if (searchType === 'Sala') {
      return (
        <Label className={'subTitle2 text-11 m-text-20 fontGray250 margt12 margb10 text-center'}>
          {lenguage === 'ES' ? 'ENCUENTRA TU' : 'FIND YOUR'}
          <br />
          {lenguage === 'ES' ? 'SALA DE JUNTAS' : 'MEETING ROOM'}
        </Label>
      );
    } else if (searchType === 'Coworking') {
      return (
        <Label className={'subTitle2 text-11 m-text-20 fontGray250 margt12 margb10 text-center'}>
          {lenguage === 'ES' ? 'ENCUENTRA TU' : 'FIND YOUR'}
          <br />
          {lenguage === 'ES' ? 'PASE DE COWORKING' : 'COWORKING PASS'}
        </Label>
      );
    } else {
      return <></>;
    }
  };

  const hours = Array.from({ length: 27 }, (_, i) => {
    const hour = Math.floor(i / 2) + 6;
    const minutes = i % 2 === 0 ? '00' : '30';
    return `${String(hour).padStart(2, '0')}:${minutes}`;
  });

  const handleSubmit = (values) => {
    //values.endTime = null;
    const spaceType = searchType === 'Sala' ? 'sala de juntas' : 'coworking';

    const searchFormData = {
      ...values,
      coordinates,
      selectedSuggestion,
      spaceType,
    };
    dispatch(setSearchFormData(searchFormData));
    ReactPixel.trackCustom('submitSearchForm', { clave: searchFormData });
    navigate(lenguage === 'ES' ? '/lista-espacios' : '/spaces-list');
  };

  return (
    <FlexContainer
      className={
        'flex-column format100 m-formatAuto bgWhite m-margt20 m-margb50 m-marglr10 padtb20'
      }
    >
      <TitleLabel />
      <FlexContainer className={'flex-column gap-18 format100 padtb20'}>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ values, errors, touched, setFieldValue }) => {
            const handleLocationChange = (e) => {
              const searchValue = e.target.value;
              if (searchValue.length > 3 && autocompleteService.current) {
                if (userLocation) {
                  autocompleteService.current.getPlacePredictions(
                    {
                      input: searchValue,
                      location: new window.google.maps.LatLng(userLocation.lat, userLocation.lng),
                      radius: 50000,
                    },
                    (predictions, status) => {
                      if (status === window.google.maps.places.PlacesServiceStatus.OK) {
                        console.log('ver predicciones: ', JSON.stringify(predictions));
                        setSuggestions(predictions);
                      }
                    },
                  );
                } else {
                  autocompleteService.current.getPlacePredictions(
                    { input: searchValue },
                    (predictions, status) => {
                      if (status === window.google.maps.places.PlacesServiceStatus.OK) {
                        setSuggestions(predictions);
                      }
                    },
                  );
                }
              }
              setSelectedSuggestion(null);
              setFieldValue('location', searchValue);
            };

            const setSelectedSuggestionxForm = (value) => {
              console.log('Esta es la ubicación seleccionada', value);
              setSelectedSuggestion(value);
              if (value?.place_id && window.google) {
                const service = new window.google.maps.places.PlacesService(
                  document.createElement('div'),
                );
                service.getDetails({ placeId: value.place_id }, (place, status) => {
                  if (status === window.google.maps.places.PlacesServiceStatus.OK) {
                    const newCoordinates = place.geometry.location.toJSON();
                    setCoordinates(newCoordinates);
                  }
                });
              }
              setFieldValue('location', value?.description);
            };

            const increaseGuests = () => setFieldValue('guests', values.guests + 1);
            const decreaseGuests = () => {
              if (values.guests > 1) {
                setFieldValue('guests', values.guests - 1);
              }
            };

            const disabledButton = activeField === 'location' && !selectedSuggestion;
            const selectFieldButtonClass = disabledButton
              ? 'm-format100 padtb10 text-18 margtb40 border-rad18px bgNeutral075 fontWhite'
              : 'm-format100 padtb10 text-18 margtb40 border-rad18px bgBlue400 fontWhite';

            const handleOpenModal = (field) => {
              setActiveField(field);
              setModalOpen(true);
              ReactPixel.trackCustom('editedField', {
                editedField: { field },
              });
            };

            const handleCloseModal = () => {
              setModalOpen(false);
              if (disabledButton) {
                setSelectedSuggestion(null);
                setFieldValue('location', '');
              }
              setActiveField('');
            };

            return (
              <Form>
                <FlexContainer
                  style={{
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '20px',
                  }}
                >
                  <TextField
                    fullWidth
                    label={
                      lenguage === 'ES'
                        ? '¿A dónde vas a ir a trabajar?'
                        : 'Where are you going to work?'
                    }
                    variant="outlined"
                    value={values.location}
                    onClick={() => handleOpenModal('location')}
                    error={touched.location && Boolean(errors.location)}
                    helperText={touched.location && errors.location}
                    InputLabelProps={{
                      sx: { fontSize: '18px', fontWeight: '500' },
                    }}
                    InputProps={{
                      sx: {
                        '& .MuiOutlinedInput-notchedOutline': {
                          legend: {
                            width: 'auto',
                            padding: '0 32px',
                          },
                        },
                      },
                      style: { fontSize: '12px' },
                      readOnly: true,
                      startAdornment: (
                        <InputAdornment position="start">
                          <LocationOnOutlinedIcon />
                        </InputAdornment>
                      ),
                    }}
                  />

                  <TextField
                    fullWidth
                    label={lenguage === 'ES' ? '¿Cuándo?' : 'When?'}
                    variant="outlined"
                    value={values.startDate}
                    onClick={() => handleOpenModal('startDate')}
                    error={touched.startDate && Boolean(errors.startDate)}
                    helperText={touched.startDate && errors.startDate}
                    InputLabelProps={{
                      sx: { fontSize: '18px', fontWeight: '500' },
                      shrink: true,
                      locale: 'es-ES',
                    }}
                    InputProps={{
                      style: { fontSize: '14px' },
                      sx: {
                        '& .MuiOutlinedInput-notchedOutline': {
                          legend: {
                            width: 'auto',
                            padding: '0 10px',
                          },
                        },
                      },
                      readOnly: true,
                      startAdornment: (
                        <InputAdornment position="start">
                          <CalendarTodayOutlinedIcon />
                        </InputAdornment>
                      ),
                    }}
                  />

                  <TextField
                    fullWidth
                    label={lenguage === 'ES' ? '¿En qué horario?' : 'At what time?'}
                    variant="outlined"
                    value={values.startTime + ' - ' + values.endTime}
                    onClick={() => handleOpenModal('endTime')}
                    error={touched.endTime && Boolean(errors.endTime)}
                    helperText={touched.endTime && errors.endTime}
                    InputLabelProps={{
                      locale: 'es-ES',
                      sx: {
                        fontSize: '18px',
                        fontWeight: '500',
                      },
                      shrink: true,
                    }}
                    InputProps={{
                      style: { fontSize: '14px' },
                      sx: {
                        '& .MuiOutlinedInput-notchedOutline': {
                          legend: {
                            width: 'auto',
                            padding: '0 10px',
                          },
                        },
                      },
                      readOnly: true,
                      startAdornment: (
                        <InputAdornment position="start">
                          <AccessTimeOutlinedIcon />
                        </InputAdornment>
                      ),
                    }}
                  />

                  <TextField
                    fullWidth
                    label={lenguage === 'ES' ? '¿Cuántas personas?' : 'How many people?'}
                    variant="outlined"
                    type="number"
                    value={values.guests}
                    onClick={() => handleOpenModal('guests')}
                    error={touched.guests && Boolean(errors.guests)}
                    helperText={touched.guests && errors.guests}
                    InputLabelProps={{
                      sx: { fontSize: '18px', fontWeight: '500' },
                    }}
                    InputProps={{
                      sx: {
                        '& .MuiOutlinedInput-notchedOutline': {
                          legend: {
                            width: 'auto',
                            padding: '0 14px',
                          },
                        },
                      },
                      style: { fontSize: '14px' },
                      readOnly: true,
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonOutlineOutlinedIcon />
                        </InputAdornment>
                      ),
                    }}
                  />

                  <button
                    className={
                      'm-format100 bgBlue400 border-rad16px borderGray050 fontWhite text-16 grayShadow16 margt10 margb20 padlr40 padtb10'
                    }
                    type="submit"
                  >
                    {lenguage === 'ES' ? 'Buscar' : 'Search'}
                  </button>
                </FlexContainer>

                <Dialog
                  open={modalOpen}
                  onClose={handleCloseModal}
                  fullScreen
                  PaperProps={{
                    sx: {
                      position: 'fixed',
                      height: '100vh',
                      width: '100vw',
                      margin: 0,
                      borderRadius: 0,
                      position: 'fixed',
                      top: 0,
                      left: 0,
                      overflow: 'hidden',
                    },
                  }}
                >
                  <DialogTitle
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      paddingTop: '60px',
                    }}
                  >
                    <IconButton
                      onClick={handleCloseModal}
                      sx={{
                        backgroundColor: '#022b54',
                        color: 'white',
                        borderRadius: '50%',
                        width: '40px',
                        height: '40px',
                        '&:hover': {
                          backgroundColor: 'darkblue',
                        },
                      }}
                    >
                      <CloseIcon />
                    </IconButton>
                    <span className={'fontGray250 subTitle3'} style={{ paddingLeft: '10px' }}>
                      {lenguage === 'ES' ? 'Selecciona ' : 'Select '}
                      {mapFieldLabel(activeField)}
                    </span>
                    <div />
                  </DialogTitle>
                  <DialogContent>
                    {activeField === 'location' && (
                      <>
                        {/* 
                        <TextField
                          fullWidth
                          value={values[activeField]}
                          onChange={(e) => handleLocationChange(e)}
                          type={'text'}
                          placeholder={
                            lenguage === 'ES'
                              ? 'Escribe aquí el lugar donde deseas buscar '
                              : 'Write here the place where you want to search '
                          }
                          InputProps={{
                            style: { fontSize: '14px' },
                          }}
                          autoFocus
                        />
                        */}
                        <TextField
                          fullWidth
                          value={values[activeField]}
                          type={'text'}
                          placeholder={
                            lenguage === 'ES'
                              ? 'Escribe aquí el lugar donde deseas buscar '
                              : 'Write here the place where you want to search '
                          }
                          InputProps={{
                            style: { fontSize: '16px' },
                            readOnly: true,
                          }}
                          autoFocus
                        />
                        <LocationSuggestions
                          //suggestions={suggestions}
                          suggestions={staticLocationsSuggestions}
                          setLocationToSearch={setSelectedSuggestionxForm}
                          lenguage={lenguage}
                        />
                      </>
                    )}
                    {activeField === 'guests' && (
                      <FlexContainer className={'gap-10'}>
                        <FlexContainer className={'format50'}>
                          {lenguage === 'ES' ? ' Personas' : ' Seats'}{' '}
                        </FlexContainer>
                        <FlexContainer className={'format50'}>
                          <Button variant="outlined" onClick={decreaseGuests}>
                            -
                          </Button>
                          <TextField
                            value={values[activeField]}
                            variant="outlined"
                            type="number"
                            onChange={(e) => setFieldValue(activeField, e.target.value)}
                            inputProps={{
                              min: 1,
                              readOnly: true,
                              fontSize: '14px',
                            }}
                          />
                          <Button variant="outlined" onClick={increaseGuests}>
                            +
                          </Button>
                        </FlexContainer>
                      </FlexContainer>
                    )}

                    {activeField === 'startDate' && (
                      <FlexContainer className="flex-column align-center formatAuto">
                        <TextField
                          type="date"
                          value={values.startDate}
                          error={customDateError !== ''}
                          onChange={(e) => {
                            const selectedDate = e.target.value;
                            if (selectedDate >= tomorrow) {
                              setFieldValue('startDate', selectedDate);
                              setCustomDateError('');
                            } else {
                              setCustomDateError('Debe ingresar una fecha posterior al día actual');
                            }
                          }}
                          autoFocus
                          InputProps={{
                            inputProps: {
                              min: tomorrow,
                              pattern: '[0-9]{4}-[0-9]{2}-[0-9]{2}',
                            },
                          }}
                          InputLabelProps={{
                            locale: 'es-ES',
                          }}
                        />
                        {customDateError !== '' && (
                          <p className={'fontRedR300'}>{customDateError}</p>
                        )}
                      </FlexContainer>
                    )}

                    {activeField === 'endTime' && (
                      <FlexContainer className="flex-column align-center formatAuto margt10 gap-20">
                        <TextField
                          select
                          fullWidth
                          label={lenguage === 'ES' ? 'Hora de entrada' : 'Check-in Time'}
                          variant="outlined"
                          value={values.startTime}
                          onChange={(e) => {
                            setFieldValue('startTime', e.target.value);
                            if (values.endTime && e.target.value >= values.endTime) {
                              setFieldValue('endTime', '');
                            }
                          }}
                          error={touched.startTime && Boolean(errors.startTime)}
                          helperText={touched.startTime && errors.startTime}
                        >
                          {hours.map((hour) => (
                            <MenuItem key={hour} value={hour}>
                              {hour}
                            </MenuItem>
                          ))}
                        </TextField>

                        <TextField
                          select
                          fullWidth
                          label={lenguage === 'ES' ? 'Hora de Salida' : 'Check-out Time'}
                          variant="outlined"
                          value={values.endTime}
                          onChange={(e) => setFieldValue('endTime', e.target.value)}
                          error={touched.endTime && Boolean(errors.endTime)}
                          helperText={touched.endTime && errors.endTime}
                        >
                          {hours
                            .filter((hour) => {
                              if (!values.startTime) return true;
                              const [startHour, startMinutes] = values.startTime
                                .split(':')
                                .map(Number);
                              const [endHour, endMinutes] = hour.split(':').map(Number);
                              return (
                                endHour * 60 + endMinutes >= startHour * 60 + startMinutes + 60
                              );
                            })
                            .map((hour) => (
                              <MenuItem key={hour} value={hour}>
                                {hour}
                              </MenuItem>
                            ))}
                        </TextField>
                        {searchType === 'Coworking' && (
                          <FlexContainer className={'text-12'}>
                            {lenguage === 'ES'
                              ? 'El precio de renta de un pase de coworking no se calculará en función a la cantidad de horas seleccionadas, la reserva puede hacerse efectiva a lo largo del día seleccionado.'
                              : 'The rental price of a coworking pass will not be calculated based on the number of selected hours; the reservation can be used at any time throughout the selected day.'}
                          </FlexContainer>
                        )}
                        {searchType === 'Sala' && (
                          <FlexContainer className={'text-12'}>
                            {lenguage === 'ES'
                              ? 'El precio de renta de una sala de juntas se calculará en función a la cantidad de horas seleccionadas.'
                              : 'The rental price of a meeting room will be calculated based on the number of selected hours.'}
                          </FlexContainer>
                        )}
                      </FlexContainer>
                    )}

                    <button
                      className={selectFieldButtonClass}
                      fullWidth
                      variant="contained"
                      color="primary"
                      disabled={disabledButton}
                      onClick={handleCloseModal}
                    >
                      {lenguage === 'ES' ? 'Seleccionar' : 'Select'}
                    </button>
                  </DialogContent>
                </Dialog>
              </Form>
            );
          }}
        </Formik>
      </FlexContainer>
    </FlexContainer>
  );
};

export default NewGeneralSearchForm;
